import * as React from 'react';
import type { MenuProps } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import { useState, useCallback, useMemo, useRef } from 'react';
import { Tabs, TabsProps, Button, Dropdown } from 'antd';

import Utils from '../../common/util/Utils';
import { RegulationOutlooks } from '../dashboard/detailed/regOutlook/RegulationOutlooks';
import { Measures } from '../dashboard/detailed/measures/Measures';
import { Hotspots } from '../dashboard/detailed/hotspots/Hotspots';
import {
    ExtendedMonitoredTv,
    MonitoredEvent,
    MonitoredMeasure,
    MonitoredRegulationOutlook,
    DashboardData,
} from '../../model/SOL49';
import { HotspotDto } from '../../model/CORE';
import { MeasuresSettings, DEFAULT_MEASURES_SETTINGS } from '../dashboard/detailed/measures/MeasuresSettings';
import { HotspotSettings, DEFAULT_HOTSPOT_SETTINGS } from '../dashboard/detailed/hotspots/HotspotSettings';
import { MonitoredEventUtils } from '../../common/util/MonitoredEventUtils';
import MeasureUtils from '../../common/util/MeasureUtils';
import { Flex } from './Flex';
import './TabManager.less';

type AntTabItem = Exclude<TabsProps['items'], undefined>[number];
export type TabItem = AntTabItem & {
    isDynamic?: boolean;
};
interface TabManagerProps {
    dashboardData: DashboardData;
    globalTextFilter: string;
    measures: MonitoredMeasure[];
    currentTime?: Date;
    monitoredEvents?: MonitoredEvent[];
    globalFilterEnabled?: boolean;
    selectedMeasures?: MonitoredMeasure[];
    measureSettings?: MeasuresSettings;
    hotspotSettings?: HotspotSettings;
    selectedHotspotTvs?: ExtendedMonitoredTv[];
    selectedRegOutlooks?: MonitoredRegulationOutlook[];

    onMeasureSelectionChange?: (selection: MonitoredMeasure[]) => void;
    onMeasureSettingsChange?: (settings: MeasuresSettings) => void;
    onHotspotSettingsChange?: (settings: HotspotSettings) => void;
    onHotspotSelectionChange?: (selection: ExtendedMonitoredTv[]) => void;
    onRegOutlookSelectionChange?: (selection: MonitoredRegulationOutlook[]) => void;
}

type TabDefinition = {
    key: string;
    type: 'REGULATION' | 'MEASURES' | 'HOTSPOTS';
};

const generateKey = (type: string, index: number): string => {
    return `${type.toLowerCase()}-${index}`;
};

const getInitialTabs = (): { tabs: TabDefinition[]; activeKey: string } => {
    const firstTab: TabDefinition = {
        key: generateKey('MEASURES', 0),
        type: 'MEASURES',
    };
    const secondTab: TabDefinition = {
        key: generateKey('HOTSPOTS', 0),
        type: 'HOTSPOTS',
    };
    const thirdTab: TabDefinition = {
        key: generateKey('REGULATION', 0),
        type: 'REGULATION',
    };
    return {
        tabs: [firstTab, secondTab, thirdTab],
        activeKey: firstTab.key,
    };
};

export const TabManager: React.FC<TabManagerProps> = ({
    dashboardData,
    globalTextFilter,
    measures,
    currentTime = new Date(),
    monitoredEvents = [],
    globalFilterEnabled = true,
    selectedMeasures = [],
    measureSettings = DEFAULT_MEASURES_SETTINGS,
    hotspotSettings = DEFAULT_HOTSPOT_SETTINGS,
    selectedHotspotTvs = [],
    selectedRegOutlooks = [],

    onMeasureSelectionChange = () => {},
    onMeasureSettingsChange = () => {},
    onHotspotSettingsChange = () => {},
    onHotspotSelectionChange = () => {},
    onRegOutlookSelectionChange = () => {},
}) => {
    const initialTabs = getInitialTabs();
    const [tabs, setTabs] = useState<TabDefinition[]>(initialTabs.tabs);
    const [activeKey, setActiveKey] = useState<string>(initialTabs.activeKey);

    const newTabIndex = useRef(1);

    const addTabMenuItems: MenuProps = {
        mode: 'horizontal',
        items: [
            {
                key: 'add-measures',
                label: 'Add Measures',
                onClick: () => addNewTab('MEASURES'),
            },
            {
                key: 'add-hotspots',
                label: 'Add Hotspots',
                onClick: () => addNewTab('HOTSPOTS'),
            },
            {
                key: 'add-regulation',
                label: 'Add Regulation',
                onClick: () => addNewTab('REGULATION'),
            },
        ],
    };

    if (Utils.isNotExists(dashboardData)) {
        return null;
    }

    const displayedData = useMemo(() => {
        return {
            displayedMeasures: dashboardData?.monitoredMeasures || ([] as MonitoredMeasure[]),
            displayedHotspots: dashboardData?.hotspots || ({} as { [tv: string]: HotspotDto[] }),
            displayedRegOutlook: dashboardData?.monitoredRegulationOutlook || ([] as MonitoredRegulationOutlook[]),
        };
    }, [dashboardData.monitoredMeasures, dashboardData.hotspots, dashboardData.monitoredRegulationOutlook]);

    const getProposalMeasureIds = useCallback((): string[] => {
        return MeasureUtils.getProposalMeasureIds(dashboardData?.monitoredMcdmMeasures);
    }, [dashboardData.monitoredMcdmMeasures]);

    const tvList = dashboardData?.monitoredTvList || [];
    const ids = tvList.map((mtv: ExtendedMonitoredTv) => mtv.refLocation.id);
    const stableTvListIds = JSON.stringify(ids);

    const getFilteredHotspotTvs = useCallback(
        (hotspots: { [p: string]: Array<HotspotDto> } | undefined): ExtendedMonitoredTv[] => {
            const hotspotsTvs = (hotspots && Object.keys(hotspots)) || [];
            return (
                dashboardData?.monitoredTvList?.filter((mtv: ExtendedMonitoredTv) =>
                    hotspotsTvs.includes(mtv.refLocation.id),
                ) || []
            );
        },
        [stableTvListIds],
    );

    const { displayedMeasures, displayedHotspots, displayedRegOutlook } = displayedData;
    const textFilter = globalFilterEnabled ? globalTextFilter : '';
    const activeEvents = MonitoredEventUtils.activeEvents(monitoredEvents);

    const memoizedRegulationOutlooks = useMemo(() => {
        return (
            <RegulationOutlooks
                regOutlook={displayedRegOutlook}
                selection={selectedRegOutlooks}
                onSelectionChange={onRegOutlookSelectionChange}
                currentTime={currentTime}
                textFilter={textFilter}
            />
        );
    }, [displayedRegOutlook, selectedRegOutlooks, onRegOutlookSelectionChange, currentTime, textFilter]);

    const memoizedMeasures = useMemo(() => {
        return (
            <Measures
                currentTime={currentTime}
                monitoredPeriod={dashboardData.period}
                measures={measures}
                proposalMeasures={getProposalMeasureIds()}
                monitoredEvents={activeEvents}
                monitoredTvList={dashboardData?.monitoredTvList}
                textFilter={textFilter}
                selectedMeasures={selectedMeasures}
                onSelectionChange={onMeasureSelectionChange}
                settings={measureSettings}
                onMeasureSettingsChange={onMeasureSettingsChange}
            />
        );
    }, [
        currentTime,
        dashboardData,
        measures,
        getProposalMeasureIds,
        activeEvents,
        textFilter,
        selectedMeasures,
        onMeasureSelectionChange,
        measureSettings,
        onMeasureSettingsChange,
    ]);

    const memoizedHotspots = useMemo(() => {
        const filteredTvs = getFilteredHotspotTvs(displayedHotspots);
        return (
            <Hotspots
                hotspots={displayedHotspots}
                currentTime={currentTime}
                monitoredPeriod={dashboardData.period}
                textFilter={textFilter}
                monitoredTvList={filteredTvs}
                selection={selectedHotspotTvs?.map(mtv => mtv.refLocation.id)}
                onSelectionChange={(tvIds: string[]) =>
                    onHotspotSelectionChange(filteredTvs.filter(mtv => tvIds.includes(mtv.refLocation.id)))
                }
                monitoredEvents={activeEvents}
                measures={displayedMeasures}
                settings={hotspotSettings}
                onHotspotSettingsChange={onHotspotSettingsChange}
            />
        );
    }, [
        displayedHotspots,
        currentTime,
        dashboardData,
        textFilter,
        getFilteredHotspotTvs,
        selectedHotspotTvs,
        onHotspotSelectionChange,
        activeEvents,
        displayedMeasures,
        hotspotSettings,
        onHotspotSettingsChange,
    ]);

    const buildTabContent = useCallback(
        (tab: TabDefinition): React.ReactNode => {
            switch (tab.type) {
                case 'REGULATION':
                    return memoizedRegulationOutlooks;
                case 'MEASURES':
                    return memoizedMeasures;
                case 'HOTSPOTS':
                    return memoizedHotspots;
                default:
                    return <div>Unknown Tab Type</div>;
            }
        },
        [memoizedRegulationOutlooks, memoizedMeasures, memoizedHotspots],
    );

    const handleTabChange = useCallback((newActiveKey: string) => {
        setActiveKey(newActiveKey);
    }, []);

    const handleTabEdit = useCallback(
        (targetKey: React.MouseEvent | React.KeyboardEvent | string, action: 'add' | 'remove') => {
            if (action === 'add') {
                const newKey = generateKey('MEASURES', newTabIndex.current++);
                const newTab: TabDefinition = { key: newKey, type: 'MEASURES' };

                setTabs(prevTabs => [...prevTabs, newTab]);
                setActiveKey(newKey);
            } else if (action === 'remove') {
                const targetKeyString = targetKey as string;

                if (tabs.length <= 1) {
                    throw new Error('Cannot close the last tab.');
                }

                const newTabs = tabs.filter(tab => tab.key !== targetKeyString);

                if (activeKey === targetKeyString) {
                    const targetIndex = tabs.findIndex(tab => tab.key === targetKeyString);
                    const newIndex = Math.max(0, targetIndex - 1);
                    setActiveKey(newTabs[newIndex].key);
                }

                setTabs(newTabs);
            }
        },
        [tabs, activeKey],
    );

    const addNewTab = useCallback((type: 'REGULATION' | 'MEASURES' | 'HOTSPOTS') => {
        const newKey = generateKey(type, newTabIndex.current++);
        const newTab: TabDefinition = { key: newKey, type: type };

        setTabs(prevTabs => [...prevTabs, newTab]);
        setActiveKey(newKey);
    }, []);
    const renderedItems: TabItem[] = useMemo(() => {
        return tabs.map(tabDefinition => {
            let label = 'Unknown';
            if (tabDefinition.type === 'REGULATION') label = 'Regulation Outlook';
            if (tabDefinition.type === 'MEASURES') label = 'Measures';
            if (tabDefinition.type === 'HOTSPOTS') label = 'Hotspots';

            return {
                key: tabDefinition.key,
                label: label,
                closable: tabs.length > 1,
                children: buildTabContent(tabDefinition),
            };
        });
    }, [tabs, buildTabContent]);

    return (
        <Flex direction="vertical" className="container">
            <div className="cta-section">
                <Dropdown menu={addTabMenuItems}>
                    <Button size="small">
                        Add Tab <DownOutlined />
                    </Button>
                </Dropdown>
            </div>
            <Flex className="main-section">
                <Tabs
                    style={{ margin: 0 }}
                    type="editable-card"
                    items={renderedItems}
                    activeKey={activeKey}
                    onChange={handleTabChange}
                    onEdit={handleTabEdit}
                    hideAdd={true}
                />
            </Flex>
        </Flex>
    );
};
